import { FC, ReactNode, useContext } from 'react'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import heroImage from '@assets/amex-great/hero.jpg'
import platinumCard from '@assets/amex-great/platinum-card.png'
import logoBluebox from '@assets/amex-great/logo-bluebox.svg'
import menuBluebox from '@assets/amex-great/menu-bluebox.svg'
import logoStack from '@assets/amex-great/logo-stack.svg'
import searchIcon from '@assets/amex-great/search.svg'
import flagEs from '@assets/amex-great/flag-es.svg'
import chevronRight from '@assets/amex-great/chevron-right.svg'
import './AmexGreat.css'

interface AmexGreatLayoutProps {
  children: ReactNode
  centered?: boolean
}

// amex.com page chrome (navigation, hero, country footer) wrapping the step card.
// The navigation is decorative only - it mirrors the Amex website the flow is embedded into.
export const AmexGreatLayout: FC<AmexGreatLayoutProps> = observer(({ children, centered }) => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { amexGreatPage: trans } = store.TranslationsState.translations

  const renderHeader = () => {
    if (isMobile) {
      return (
        <div className="amex-great__header-inner">
          <div className="amex-great__header-group">
            <img src={menuBluebox} alt="" width={48} height={48} />
            <img src={logoStack} alt="American Express" width={86} height={24} />
          </div>
          <div className="amex-great__header-group amex-great__header-group--right">
            <img src={searchIcon} alt="" width={18} height={18} />
            <span className="amex-great__header-link">{trans.layout.logIn}</span>
          </div>
        </div>
      )
    }

    return (
      <div className="amex-great__header-inner">
        <div className="amex-great__header-group">
          <img src={logoBluebox} alt="American Express" width={40} height={40} />
          {trans.layout.navigation.map((item) => (
            <span key={item} className="amex-great__header-link">
              {item}
            </span>
          ))}
        </div>
        <div className="amex-great__header-group amex-great__header-group--right">
          <img src={searchIcon} alt="" width={18} height={18} />
          <span className="amex-great__header-link">{trans.layout.customerService}</span>
          <span className="amex-great__header-login">{trans.layout.logIn}</span>
        </div>
      </div>
    )
  }

  const renderIntro = () => {
    const productImage = (
      <img className="amex-great__product-image" src={platinumCard} alt={trans.layout.cardName} />
    )

    if (isMobile) {
      return (
        <div className="amex-great__intro">
          <h1 className="amex-great__title">{trans.layout.cardApplication}</h1>
          {productImage}
        </div>
      )
    }

    return (
      <div className="amex-great__intro">
        <div className="amex-great__product">
          {productImage}
          <p className="amex-great__product-name">{trans.layout.cardName}</p>
        </div>
        <h1 className="amex-great__title">{trans.layout.cardApplication}</h1>
      </div>
    )
  }

  return (
    <div className={cx('amex-great', isMobile && 'amex-great--mobile')}>
      <header className="amex-great__header" aria-hidden="true">
        {renderHeader()}
      </header>

      <div className="amex-great__body">
        <div className="amex-great__hero-bg">
          <img src={heroImage} alt="" />
        </div>

        <div className="amex-great__column">
          {renderIntro()}
          <div className={cx('amex-great__card', centered && 'amex-great__card--centered')}>
            {children}
          </div>
        </div>
      </div>

      {!isMobile && (
        <footer className="amex-great__footer" aria-hidden="true">
          <div className="amex-great__footer-inner">
            <img src={flagEs} alt="" width={20} height={14.2857} />
            <span className="amex-great__footer-country">{trans.layout.country}</span>
            <span className="amex-great__footer-change">
              {trans.layout.changeCountry}
              <img src={chevronRight} alt="" width={24} height={24} />
            </span>
          </div>
        </footer>
      )}
    </div>
  )
})
